import { useState, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

export interface GalleryPhoto {
  id: string;
  thumbnail_url: string;
  url_full_size: string;
}

interface PhotoGalleryProps {
  photos: GalleryPhoto[];
  className?: string;
}

export function PhotoGallery({ photos, className }: PhotoGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const current =
    openIndex === null
      ? undefined
      : photos[Math.min(openIndex, photos.length - 1)];
  const position = current ? photos.indexOf(current) : -1;

  const step = (delta: number) =>
    setOpenIndex(
      (position + delta + photos.length) % Math.max(photos.length, 1),
    );

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    }
  };

  return (
    <>
      <div
        className={cn(
          'tw:grid tw:grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] tw:gap-2',
          className,
        )}
      >
        {photos.map((photo, index) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setOpenIndex(index)}
            className="tw:aspect-square tw:overflow-hidden tw:rounded-lg tw:bg-muted tw:ring-1 tw:ring-foreground/10 tw:outline-none tw:transition-opacity tw:hover:opacity-90 tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
            aria-label={i18n.t('PhotoGallery:openPhoto', {
              index: index + 1,
              count: photos.length,
            })}
          >
            <img
              src={photo.thumbnail_url}
              alt=""
              loading="lazy"
              className="tw:size-full tw:object-cover"
            />
          </button>
        ))}
      </div>

      <Dialog
        open={current !== undefined}
        onOpenChange={(open) => {
          if (!open) setOpenIndex(null);
        }}
      >
        <DialogContent
          className="tw:sm:max-w-4xl"
          onKeyDown={handleKeyDown}
          aria-describedby={undefined}
        >
          <DialogTitle className="tw:text-base tw:font-medium">
            {i18n.t('PhotoGallery:position', {
              index: position + 1,
              count: photos.length,
            })}
          </DialogTitle>
          {current && (
            <img
              src={current.url_full_size}
              alt=""
              className="tw:max-h-[75vh] tw:w-full tw:rounded-lg tw:bg-muted tw:object-contain"
            />
          )}
          {photos.length > 1 && (
            <div className="tw:flex tw:justify-between">
              <Button variant="secondary" onClick={() => step(-1)}>
                <ChevronLeft data-icon="inline-start" />
                {i18n.t('PhotoGallery:previous')}
              </Button>
              <Button variant="secondary" onClick={() => step(1)}>
                {i18n.t('PhotoGallery:next')}
                <ChevronRight data-icon="inline-end" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
